import EditIcon from '@mui/icons-material/Edit';
import {
  Alert,
  Box,
  Button,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  FormGroup,
  IconButton,
  List,
  ListItem,
  Typography,
} from '@mui/material';
import { type InputHTMLAttributes, useId, useState } from 'react';
import {
  OPTED_IN_DOCUMENT_TYPE_LABELS,
  OPTED_IN_DOCUMENT_TYPES_COPY as COPY,
} from './optedInDocumentTypesCopy';

export interface OptedInDocumentTypesSectionProps<T extends string = string> {
  /** The opt-in types the picker offers, in the order shown. Only these get a checkbox. */
  availableDocumentTypes: readonly T[];
  selectedDocumentTypes: readonly T[];
  /**
   * Saves the complete list of opted-in types. Rejects when the save fails. The parent has
   * already told the user, so this section only keeps the Edit step open with the staged list
   * and shows its own inline error.
   */
  onDocumentTypesSave: (documentTypes: T[]) => Promise<void>;
  dataTestId?: string;
}

const labelOf = (documentType: string) =>
  OPTED_IN_DOCUMENT_TYPE_LABELS[documentType] ?? documentType;

// ADR-0071: a data-field value is kebab-case, and a document type value is PascalCase.
const toKebabCase = (value: string) => value.replace(/([a-z0-9])([A-Z])/g, '$1-$2').toLowerCase();

// The checkbox input's props, typed to allow the data-field attribute that its own type leaves out.
const checkboxInputProps = (
  documentType: string,
): InputHTMLAttributes<HTMLInputElement> & Record<`data-${string}`, string> => ({
  'data-field': `opted-in-${toKebabCase(documentType)}`,
});

export function OptedInDocumentTypesSection<T extends string = string>({
  availableDocumentTypes,
  selectedDocumentTypes,
  onDocumentTypesSave,
  dataTestId = 'opted-in-document-types-section',
}: Readonly<OptedInDocumentTypesSectionProps<T>>) {
  const [isEditing, setIsEditing] = useState(false);
  const [stagedDocumentTypes, setStagedDocumentTypes] = useState<readonly T[]>([]);
  const [hasSaveError, setHasSaveError] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const editHelperId = useId();

  if (availableDocumentTypes.length === 0) {
    return null;
  }

  const inPickerOrder = (documentTypes: readonly T[]) =>
    availableDocumentTypes.filter((type) => documentTypes.includes(type));
  // Read mode lists only the types the picker offers: a saved type it does not offer cannot be
  // changed here, so it is neither shown nor editable.
  const savedDocumentTypes = inPickerOrder(selectedDocumentTypes);
  // A save replaces the whole list, so a saved type the picker does not offer is kept: the list
  // sent is the staged offered types in picker order, then the unoffered saved types as they were.
  const withUnofferedSavedTypes = (offeredDocumentTypes: T[]) => [
    ...offeredDocumentTypes,
    ...selectedDocumentTypes.filter((type) => !availableDocumentTypes.includes(type)),
  ];

  const openEditor = () => {
    setStagedDocumentTypes(selectedDocumentTypes);
    setHasSaveError(false);
    setIsSaving(false);
    setIsEditing(true);
  };

  const closeEditor = () => setIsEditing(false);

  // Escape and a click on the backdrop also close the Edit step, but not while a save runs: the
  // dialog would go while the save can still fail and must show its error.
  const handleDialogClose = () => {
    if (!isSaving) {
      closeEditor();
    }
  };

  const handleToggle = (documentType: T, isChecked: boolean) => {
    setStagedDocumentTypes((staged) =>
      isChecked ? [...staged, documentType] : staged.filter((type) => type !== documentType),
    );
  };

  const handleSave = async () => {
    setIsSaving(true);
    setHasSaveError(false);
    try {
      await onDocumentTypesSave(withUnofferedSavedTypes(inPickerOrder(stagedDocumentTypes)));
      closeEditor();
    } catch {
      // The parent has already reported the failure. Stay in the Edit step with the staged list and
      // show the inline error.
      setHasSaveError(true);
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <Box data-testid={dataTestId}>
      <Box
        sx={(theme) => ({
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          marginBottom: theme.semantic.spacing.xs,
        })}
      >
        <Typography variant="subtitle1">{COPY.title}</Typography>
        <IconButton
          size="small"
          onClick={openEditor}
          data-action="edit-opted-in-document-types"
          aria-label={COPY.editButtonLabel}
          color="primary"
        >
          <EditIcon fontSize="small" />
        </IconButton>
      </Box>

      <Typography
        variant="caption"
        sx={(theme) => ({
          display: 'block',
          marginBottom: theme.semantic.spacing.sm,
          color: theme.palette.text.secondary,
        })}
      >
        {COPY.helperText}
      </Typography>

      {/* Hidden while the dialog is open, so the admin sees one list of types and not two. */}
      {!isEditing &&
        (savedDocumentTypes.length > 0 ? (
          <List dense disablePadding>
            {savedDocumentTypes.map((documentType) => (
              <ListItem
                key={documentType}
                disableGutters
                sx={(theme) => ({
                  borderBottom: `${theme.semantic.spacing['4xs']} solid ${theme.palette.divider}`,
                })}
              >
                <Typography variant="body2">{labelOf(documentType)}</Typography>
              </ListItem>
            ))}
          </List>
        ) : (
          <Typography variant="body2" sx={(theme) => ({ color: theme.palette.text.secondary })}>
            {COPY.emptyState}
          </Typography>
        ))}

      <Dialog
        open={isEditing}
        onClose={handleDialogClose}
        fullWidth
        maxWidth="sm"
        transitionDuration={0}
      >
        <DialogTitle>{COPY.title}</DialogTitle>
        <DialogContent>
          {hasSaveError && (
            <Alert severity="error" sx={(theme) => ({ marginBottom: theme.semantic.spacing.sm })}>
              {COPY.saveError}
            </Alert>
          )}
          <Typography
            id={editHelperId}
            variant="caption"
            sx={(theme) => ({
              display: 'block',
              marginBottom: theme.semantic.spacing.sm,
              color: theme.palette.text.secondary,
            })}
          >
            {COPY.editHelperText}
          </Typography>
          <FormGroup role="group" aria-label={COPY.title} aria-describedby={editHelperId}>
            {availableDocumentTypes.map((documentType) => (
              <FormControlLabel
                key={documentType}
                label={labelOf(documentType)}
                control={
                  <Checkbox
                    checked={stagedDocumentTypes.includes(documentType)}
                    disabled={isSaving}
                    onChange={(event) => handleToggle(documentType, event.target.checked)}
                    inputProps={checkboxInputProps(documentType)}
                  />
                }
              />
            ))}
          </FormGroup>
        </DialogContent>
        <DialogActions>
          <Button
            variant="outlined"
            onClick={closeEditor}
            disabled={isSaving}
            data-action="cancel-opted-in-document-types"
          >
            {COPY.cancelButton}
          </Button>
          <Button
            variant="contained"
            onClick={handleSave}
            disabled={isSaving}
            data-action="save-opted-in-document-types"
          >
            {isSaving ? COPY.savingButton : COPY.saveButton}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
